import { formatLocalizedNumber } from "@common/helpers/i18n";
import { BadgeProps } from "@common/types";
import { badgeVariants } from "@app/components/ui/badge";
import { cn } from "@app/lib/utils";

type BadgeTone =
  | "neutral"
  | "primary"
  | "success"
  | "danger"
  | "warning"
  | "info";
type BadgeSize = NonNullable<BadgeProps["size"]>;

const variantMap = {
  primary: "default",
  success: "success",
  danger: "destructive",
  warning: "warning",
  info: "info",
  light: "secondary",
  dark: "secondary",
  default: "secondary",
  "outline-primary": "outlinePrimary",
  "outline-success": "outlineSuccess",
  "outline-danger": "outlineDestructive",
  "outline-warning": "outlineWarning",
  "outline-info": "outlineInfo",
  "outline-light": "outlineMuted",
  "outline-dark": "outlineMuted",
} as const;

const toneVariantMap = {
  neutral: "outline-light",
  primary: "primary",
  success: "success",
  danger: "danger",
  warning: "warning",
  info: "info",
} as const satisfies Record<BadgeTone, keyof typeof variantMap>;

const statusDotClassName = {
  neutral: "bg-muted-foreground",
  primary: "bg-primary",
  success: "bg-success",
  danger: "bg-destructive",
  warning: "bg-warning",
  info: "bg-info",
} as const satisfies Record<BadgeTone, string>;

const localizedDigits: Record<string, string> = {
  "\u06f0": "0",
  "\u06f1": "1",
  "\u06f2": "2",
  "\u06f3": "3",
  "\u06f4": "4",
  "\u06f5": "5",
  "\u06f6": "6",
  "\u06f7": "7",
  "\u06f8": "8",
  "\u06f9": "9",
  "\u0660": "0",
  "\u0661": "1",
  "\u0662": "2",
  "\u0663": "3",
  "\u0664": "4",
  "\u0665": "5",
  "\u0666": "6",
  "\u0667": "7",
  "\u0668": "8",
  "\u0669": "9",
};

function getCounterNumericValue(value: React.ReactNode) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value !== "string") {
    return undefined;
  }

  const normalizedValue = value
    .trim()
    .replace(/[\u06f0-\u06f9\u0660-\u0669]/g, (digit) =>
      localizedDigits[digit] || digit,
    )
    .replace(/[,\u060c\u066c\s]/g, "");

  if (!normalizedValue) {
    return undefined;
  }

  const numericValue = Number(normalizedValue);

  return Number.isFinite(numericValue) ? numericValue : undefined;
}

function getBadgeSize(
  size: BadgeProps["size"],
  padding: BadgeProps["padding"],
) {
  if (size) return size;
  if (padding === "large") return "lg";
  if (padding === "small") return "sm";
  if (padding === "none") return "none";
  return "md";
}

export function Badge({
  variant,
  tag,
  icon,
  children,
  className,
  padding = "medium",
  size,
  rounded = true,
  circle,
  ...rest
}: BadgeProps &
  (
    | React.HTMLAttributes<HTMLDivElement>
    | React.HTMLAttributes<HTMLSpanElement>
    | React.HTMLAttributes<HTMLParagraphElement>
  )) {
  const Component: any = tag || "div";
  return (
    <Component
      className={cn(
        badgeVariants({
          variant: variantMap[variant || "default"],
          size: getBadgeSize(size, padding),
          shape: circle ? "pill" : "default",
        }),
        !rounded && !circle && "rounded-none",
        className,
      )}
      {...rest}
    >
      {icon ? (
        <span className="inline-flex min-w-0 items-center gap-1.5">
          {icon}
          {children && <span className="min-w-0 truncate">{children}</span>}
        </span>
      ) : (
        children
      )}
    </Component>
  );
}

export function StatusDot({
  tone = "neutral",
  className,
}: {
  tone?: BadgeTone;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "h-2 w-2 shrink-0 rounded-full ring-2 ring-card",
        statusDotClassName[tone],
        className,
      )}
    />
  );
}

export function StatusBadge({
  tone = "neutral",
  dot = true,
  icon,
  children,
  size = "sm",
  className,
  ...rest
}: {
  tone?: BadgeTone;
  dot?: boolean;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  size?: BadgeSize;
  className?: string;
} & React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <Badge
      tag="span"
      variant={toneVariantMap[tone]}
      size={size}
      icon={icon || (dot ? <StatusDot tone={tone} /> : undefined)}
      className={className}
      {...rest}
    >
      {children}
    </Badge>
  );
}

export function CounterBadge({
  value,
  max,
  prefix,
  suffix,
  tone = "neutral",
  size = "sm",
  className,
  ...rest
}: {
  value: React.ReactNode;
  max?: number;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  tone?: BadgeTone;
  size?: BadgeSize;
  className?: string;
} & React.HTMLAttributes<HTMLSpanElement>) {
  const numericValue = getCounterNumericValue(value);
  const hasOverflow =
    numericValue !== undefined &&
    typeof max === "number" &&
    Number.isFinite(max) &&
    numericValue >= max;
  const displayValue =
    hasOverflow && prefix === undefined
      ? `+${formatLocalizedNumber(max, { useGrouping: false })}`
      : hasOverflow
        ? formatLocalizedNumber(max, { useGrouping: false })
        : typeof numericValue === "number"
          ? formatLocalizedNumber(numericValue, { useGrouping: false })
          : value;
  const displayPrefix =
    hasOverflow && prefix === undefined ? undefined : prefix;

  return (
    <Badge
      tag="span"
      variant={toneVariantMap[tone]}
      size={size}
      circle
      className={cn(
        "tabular-nums",
        hasOverflow && prefix === undefined && "min-w-6 px-1",
        className,
      )}
      {...rest}
    >
      {hasOverflow && prefix === undefined ? (
        <span dir="ltr">{displayValue}</span>
      ) : (
        <>
          {displayPrefix}
          {displayValue}
        </>
      )}
      {suffix}
    </Badge>
  );
}
